import { useEffect, useMemo, useState } from "react";
import { SupplierInvoiceRecordServices } from "../../services/supplier-invoice-record";
import { DEFAULT_PAGINATION } from "../../constants/pagination";
import { debounce, formatDateFieldsFlat, formatNumberFixed, renderClassNameStatusSir } from "../../utils/helper";
import { Button, Flex, Input, Space, Table } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faEye, faFilter, faPen, faPlus, faSearch, faTimeline } from "@fortawesome/free-solid-svg-icons";
import { IColumnConfig, ISortInfo } from "../../interfaces/common";
import CustomizeTableModal from "../../components/customize-table";
import { ColumnType } from "antd/es/table";
import FilterDrawer from "../../components/filter-drawer";
import { TableViewFilters } from "../../interfaces/table-view";
import TableViewDropdown from "../../components/table-view-dropdown";
import { useTableViews } from "../../hooks/useTableViews";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../constants/common";
import { useUpdateTableView } from "../../hooks/useTableView";
import { countNonEmptyFields } from "../../utils/helper";
import { ISupplierInvoiceRecordResponse, ISupplierInvoiceRecordResult } from "../../interfaces/supplier-invoice-record";
import BackButton from "../../components/back-button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageStore } from "../../context/PageStoreContext";
import { useDropdownsSirData } from "../../hooks/useDropdownsSirData";
import FilterSir from "./components/filter-sir";
import CreateAndEditInvoice from "./components/create-and-edit";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";

const COL_FIXED_LEFT = ["supplierCode", "supplierName"];
const TABLE_NAME = "supplier_invoice_records";

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = ["invoiceDate"];
  const numberFields = ["amount"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value ? formatNumberFixed(value) : value}</div>;
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusSir(value)}>{value}</span>;
  }

  return value;
};

const setWidthFieldTable = (fieldName: string) => {
  if (["supplierName"].includes(fieldName)) {
    return 300;
  }
  if (["supplierCode", "currency"].includes(fieldName)) {
    return 100;
  }
  if (["status"].includes(fieldName)) {
    return 120;
  }
  if (["leadPaxName"].includes(fieldName)) {
    return 145;
  }
  return 120;
};

const SupplierInvoiceRecordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierCode = searchParams.get("code");
  const supplierName = searchParams.get("name");
  const invoiceId = searchParams.get("invoice_id");
  const [listEsp, setListEsp] = useState<ISupplierInvoiceRecordResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isModalCreateAndEdit, setIsModalCreateAndEdit] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>();
  const [isEditSir, setIsEditSir] = useState<boolean>(true);
  const hasInvoiceEditor = useHasPermission([PERMISSIONS.INVOICE_EDITOR]);
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);

  const { setPageValue, getPageValue } = usePageStore();

  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [] } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters),
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
        invoiceId: invoiceId,
        type: "Common",
      };
      const response: ISupplierInvoiceRecordResponse = await SupplierInvoiceRecordServices.getAll(params);
      setListEsp(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTableView, debouncedSearchValue, filters, pagination.current, pagination.pageSize, sortInfo, invoiceId]);

  const { data: dropdownsData } = useDropdownsSirData(isFetchedMaterData);
  const { currencies = [], suppliers = [] } = dropdownsData || {};

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const colActive: ColumnType<ISupplierInvoiceRecordResult> = {
    title: "Actions",
    key: "action",
    width: 80,
    fixed: "right",
    align: "center",
    render: (_: any, record: ISupplierInvoiceRecordResult) => (
      <Space size="small" className="box-action" align="center">
        <Button type="link" size="small" onClick={() => handleCreateOrEditSir(record.invoiceId)}>
          {hasInvoiceEditor ? <FontAwesomeIcon icon={faPen} /> : <FontAwesomeIcon icon={faEye} />}
        </Button>
        <div>|</div>

        <Button
          type="link"
          size="small"
          onClick={() => linkTo(record.id, record.supplierName ?? "", record.supplierCode ?? "")}
        >
          <FontAwesomeIcon icon={faTimeline} />
        </Button>
      </Space>
    ),
  };

  const columns: ColumnType<ISupplierInvoiceRecordResult>[] = useMemo(() => {
    return columnConfigs
      .filter((col) => col.isDisplay)
      .map((col) => {
        return {
          title: col.label,
          dataIndex: col.column,
          key: col.column,
          fixed: COL_FIXED_LEFT.includes(col.column) ? "left" : false,
          sorter: true,
          sortDirections: ["ascend", "descend"],
          ...(col.column === "bookingNo"
            ? {
                render: (_: any, record: ISupplierInvoiceRecordResult) => {
                  let bookingNo = record.bookingNo;
                  if (!!record?.sirRefId) {
                    bookingNo = bookingNo ? bookingNo + "-CR" : "CR";
                  }
                  return bookingNo;
                },
              }
            : { render: (value: any) => renderFieldTable(col.column, value) }),
          ...(setWidthFieldTable(col.column) && { width: setWidthFieldTable(col.column) }),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnConfigs]);

  const activeFilterCount = countNonEmptyFields(filters);

  const linkTo = (link: string, supplierName: string, supplierCode: string) => {
    setPageValue({
      searchValue,
      pagination,
    });
    navigate(
      `/supplier-invoice-record/${link}/audit-trail?name=${encodeURIComponent(supplierName)}&code=${encodeURIComponent(
        supplierCode
      )}`
    );
  };

  const handleCreateOrEditSir = (invoiceId: string, edit: boolean = true) => {
    setCurrentInvoiceId(invoiceId);
    setIsEditSir(edit);
    setIsModalCreateAndEdit(true);
  };

  return (
    <div className="esp-page">
      <BackButton label="Invoices list" to="/invoice" />
      <h2 className="title-page">Supplier Invoice Record</h2>
      {(supplierCode || supplierName) && (
        <Flex
          gap={24}
          justify="space-between"
          align="center"
          style={{ marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid var(--text-black-100)" }}
        >
          <h4 style={{ margin: 0 }}>
            {decodeURIComponent(supplierCode ?? "")} - {decodeURIComponent(supplierName ?? "")}
          </h4>
          {hasInvoiceEditor && (
            <Button type="primary" onClick={() => handleCreateOrEditSir(invoiceId || "", false)}>
              <FontAwesomeIcon icon={faPlus} /> Create
            </Button>
          )}
        </Flex>
      )}

      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Booking Ref., Supp. Code, Supp. Name, Invoice Number or Lead Passenger"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 700 }}
          size="large"
          allowClear
        />
        <Flex gap={12}>
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => refetchTableViews()}
          />
        </Flex>
      </Flex>

      <Table
        dataSource={listEsp}
        loading={loading || loadingTableView}
        rowKey={(record) => record.id}
        columns={[...columns, colActive]}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(record, index) => {
          let className = index % 2 === 0 ? "highlight-row" : "";
          return className.trim();
        }}
        scroll={{ x: 1200 }}
      />

      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={columnConfigs}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {openFilter && (
        <FilterDrawer
          title="Filter Supplier Invoice Record"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          values={filters}
          children={<FilterSir dropdownsData={dropdownsData} />}
        />
      )}
      {currentInvoiceId && (
        <CreateAndEditInvoice
          isVisible={isModalCreateAndEdit}
          onClose={(isTrigger) => {
            setIsModalCreateAndEdit(false);
            if (isTrigger) {
              fetchData();
            }
          }}
          id={currentInvoiceId}
          currencies={currencies}
          suppliers={suppliers}
          isEdit={isEditSir}
          isPermissionEdit={hasInvoiceEditor}
        />
      )}
    </div>
  );
};

export default SupplierInvoiceRecordPage;
